import { useContext, useEffect } from "react";

import {
  Assignment,
  CameraEnhance,
  Favorite,
  FindInPage,
  Handshake,
  PrivacyTip,
  Inventory,
  VideoCall,
} from "@mui/icons-material";
import ChatIcon from "@mui/icons-material/Chat";
import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { sanitizeRedirectPath } from "@rubi/utils";
import { useNavigate } from "react-router-dom";

import { getServiceConfig } from "@/common/serviceConfigurations";
import { Permission } from "@/components/UserManagement/types";
import { userPermissionChecks } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { useSidebar } from "@/contexts/SidebarContext";

import AppCard from "./AppCard";
import styles from "./AppSelector.module.scss";

const AppSelector = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { setCollapsed } = useSidebar();

  const { currentUser, redirectionPath, setRedirectionPath } =
    useContext(AuthStateContext);
  const { isDevEnv } = useDevEnvironment();

  const currentUserPermissions =
    currentUser?.permissions?.map((permission) => permission.title) || [];

  const userPermissions = userPermissionChecks(currentUserPermissions);

  const organizationConfig = getServiceConfig(
    currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );
  const organizationAppPermissions = organizationConfig?.apps ?? [];

  useEffect(() => {
    if (redirectionPath && setRedirectionPath) {
      setRedirectionPath("");
      navigate(sanitizeRedirectPath(redirectionPath));
    }
  }, [redirectionPath]);

  const openSidebar = () => {
    if (!isSmall) {
      setCollapsed(false);
    }
  };

  let reportV2NavigateTo = "/rubireportv2/reports";
  if (userPermissions.canViewReportsRV2) {
    reportV2NavigateTo = "/rubireportv2/reports";
  } else if (userPermissions.canViewCitizenEngageRV2) {
    reportV2NavigateTo = "/rubireportv2/citizen-engage";
  }

  let intelNavigateTo = "/rubiintel/ocis";
  if (userPermissions.canViewEntriesIntel) {
    intelNavigateTo = "/rubiintel/reports";
  }
  // TODO: get a list of apps from BE
  const apps = [
    {
      title: "RubiREPORT",
      description: "Online Reporting System",
      navigateTo: reportV2NavigateTo,
      chip: null,
      disabled:
        !userPermissions.canAccessReportsV2App ||
        !organizationAppPermissions.includes(Permission.REPORT_V2),
      icon: <Assignment className={styles.icon} fontSize="large" />,
    },
    {
      title: "RubiINTEL",
      description: "Intelligence Management System",
      navigateTo: intelNavigateTo,
      chip: null,
      disabled: !userPermissions.canAccessIntelApp,
      icon: <FindInPage className={styles.icon} fontSize="large" />,
    },
    {
      title: "RubiALERT",
      description: "Bulletin Management System",
      navigateTo: "/rubialert/alerts",
      chip: null,
      disabled:
        !userPermissions.canAccessAlertsApp ||
        !organizationAppPermissions.includes(Permission.ALERT),
      icon: <PrivacyTip className={styles.icon} fontSize="large" />,
    },
    {
      title: "RubiCANVAS",
      description: "Camera Registry",
      navigateTo: "/rubicanvas/map",
      chip: "beta",
      disabled:
        !userPermissions.canAccessCameraRegistryApp ||
        !organizationAppPermissions.includes(Permission.CAMERA_REGISTRY),
      icon: <CameraEnhance className={styles.icon} fontSize="large" />,
    },
    {
      title: "RubiREGISTRY",
      description: "Registry System",
      navigateTo: "/rubiregistry/registries",
      chip: null,
      disabled:
        !userPermissions.canAccessRegistryApp ||
        !organizationAppPermissions.includes(Permission.REGISTRY),
      icon: <Inventory className={styles.icon} fontSize="large" />,
    },
    {
      title: "RubiMESSAGE",
      description: "Message System",
      navigateTo: "/rubimessage",
      chip: null,
      disabled:
        !userPermissions.canAccessMessageApp ||
        !organizationAppPermissions.includes(Permission.MESSAGE),
      icon: <ChatIcon className={styles.icon} fontSize="large" />,
    },
    {
      title: "RubiCONNECT",
      description: "Video Calls",
      navigateTo: "/rubiconnect",
      chip: "beta",
      disabled:
        !userPermissions.canAccessConnectApp ||
        !organizationAppPermissions.includes(Permission.CONNECT),
      icon: <VideoCall className={styles.icon} fontSize="large" />,
    },
    // RubiFEEDBACK — dev environment only until it ships.
    ...(isDevEnv
      ? [
          {
            title: "RubiFEEDBACK",
            description: "Community Feedback",
            navigateTo: "/rubifeedback",
            chip: "beta",
            // TODO: re-enable permission gating once backend issues feedback.* perms
            // disabled:
            //   !userPermissions.canAccessFeedbackApp ||
            //   !organizationAppPermissions.includes(Permission.FEEDBACK),
            disabled: false,
            icon: <Favorite className={styles.icon} fontSize="large" />,
          },
        ]
      : []),
    {
      title: "RubiPARTNER",
      description: "Partner Reporting",
      navigateTo: "/rubipartner/reports",
      chip: "beta",
      disabled: !userPermissions.canAccessPartnerApp,
      icon: <Handshake className={styles.icon} fontSize="large" />,
    },
  ];

  apps.sort((a, b) => {
    if (a.disabled && !b.disabled) {
      return 1;
    } else if (!a.disabled && b.disabled) {
      return -1;
    } else {
      return 0;
    }
  });

  return (
    <Box
      sx={{
        padding: theme.spacing(4, 2),
        margin: "0 auto",
        maxWidth: "var(--page-max-width)",
        width: "100%",
      }}
    >
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
          Select an App to Open
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(300px, 1fr))",
          },
          gap: theme.spacing(3),
          width: "100%",
        }}
      >
        {apps.map((app, index) => (
          <AppCard
            key={index}
            title={app.title}
            description={app.description}
            icon={app.icon}
            chip={app.chip}
            disabled={app.disabled}
            onClick={() => {
              openSidebar();
              navigate(app.navigateTo);
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AppSelector;
